import React, { memo, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TextField,
  Typography,
  IconButton,
  SnackbarContent,
  Alert,
  Snackbar,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  deleteUploadList,
  downloadFileAPI,
  getDeleteAttentionDetails,
  getMinAndMaxDate,
  GetUploadList,
} from "../../API/nimble";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useLoaderData } from "react-router-dom";
import ImportFilesDialog from "../../Dialogs/ImportFilesDialog";
import { enqueueSnackbar, SnackbarProvider } from "notistack";
import AttentionNeededDialog from "../../Dialogs/AttentionNeededDialog";
import {
  DateFormater,
  DateRangeEnum,
  Loader,
  sharedProps,
} from "../../Components/Components";
import { ViewTransactionsRanges } from "../../Components/Components";
import Pagination from "@mui/material/Pagination";
import BillScreenViewer from "../../Dialogs/BillScreenViewer";
import deleteIcon from "../../Images/delete.png";
import downloadIcon from "../../Images/download.png";
import { bgcolor, fontFamily } from "@mui/system";

const BillScreenURL = process.env.REACT_APP_BILLS_SCREEN_URL;

const Dcp = () => {
  const {
    token,
    corporations,
    authId,
    urlInfo,
    menuID,
    headerCrpId,
    privileges,
    preferences,
    getUserApprovalType,
    billPrivileges,
    getBillPrivileges,
    setSelect,
  } = useLoaderData();
  const transactions = ViewTransactionsRanges();
  const statuses = [
    { id: "", name: "All" },
    { id: 0, name: "Posted" },
    { id: 1, name: "Inprogress" },
    { id: 2, name: "Attention needed" },
  ];
  const [loading, setLoading] = useState(false);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const selectOption = setSelect;
  const [corporation, setCorporation] = useState(selectOption);
  const [uploadedData, setUploadedData] = useState([]);
  const [attentionDialogeOpen, setAttentionDialogeOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [billScreenUrlDialog, setBillScreenUrlDialog] = useState(null);
  const [billName, setBillName] = useState("");

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [tempFromDate, setTempFromDate] = useState(dayjs());
  const [tempToDate, setTempToDate] = useState(dayjs());

  const [prevValidFromDate, setPrevValidFromDate] = useState(null);
  const [prevValidToDate, setPrevValidToDate] = useState(null);

  const prevFromDate = useRef(null);
  const prevToDate = useRef(null);
  const prevTempFromDate = useRef(null);
  const prevTempToDate = useRef(null);

  useEffect(() => {
    if (fromDate?.isValid()) prevFromDate.current = fromDate;
    if (toDate?.isValid()) prevToDate.current = toDate;
    if (tempFromDate?.isValid()) prevTempFromDate.current = tempFromDate;
    if (tempToDate?.isValid()) prevTempToDate.current = tempToDate;
    setTempToDate(tempToDate);
    setTempFromDate(tempFromDate);
  }, [tempFromDate, tempToDate]);

  const defaultDate =
    transactions.find(
      (d) => DateRangeEnum[d.date] === preferences?.viewGridRange
    ) || transactions.find((d) => d.date === "This Year – to – Date");

  const [transaction, setTransaction] = useState(defaultDate);
  // const [transaction, setTransaction] = useState(transactions.find(t => t.date === "This Month"));

  const [statusFilter, setStatusFilter] = useState(statuses[0]);
  const [page, setPage] = useState(1);
  const rowsPerPage =
    preferences?.pageCount && preferences.pageCount > 0
      ? preferences.pageCount
      : 15;

  useEffect(() => {
    if (headerCrpId === "000000000000000000000000000000000000") {
      setCorporation(selectOption);
    } else {
      const matchedCorp = corporations.find((corp) => corp.ID === headerCrpId);
      if (matchedCorp) {
        setCorporation(matchedCorp);
      } else {
        setCorporation(selectOption);
      }
    }
  }, [headerCrpId, corporations]);

  useEffect(() => {
    if (!transaction) return;
    const { start, end } = transaction.actualValue || {};
    if (start) {
      setTempFromDate(start);
      prevFromDate.current = start;
      prevTempFromDate.current = start;
      setPrevValidFromDate(start);
    }
    if (end) {
      setTempToDate(end);
      prevToDate.current = end;
      prevTempToDate.current = end;
      setPrevValidToDate(end);
    }
  }, [transaction]);

  const getUploadedGridData = async () => {
    try {
      setLoading(true);
      const corpId = corporation?.ID;
      if (!corpId || corpId === "select" || corpId === selectOption.ID) {
        return;
      }
      const fDate = DateFormater(tempFromDate);
      const tDate = DateFormater(tempToDate);

      // Update previous valid dates state - these are the dates being used in API call, so they are valid
      if (tempFromDate?.isValid()) {
        setPrevValidFromDate(tempFromDate);
      }
      if (tempToDate?.isValid()) {
        setPrevValidToDate(tempToDate);
      }

      const response = await GetUploadList(
        corpId,
        fDate,
        tDate,
        statusFilter.id,
        token
      );

      if (response?.status === "success" && Array.isArray(response.data)) {
        setUploadedData(response.data);
        return response.data;
      }
    } catch (error) {
      enqueueSnackbar("Error importList", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      corporation?.ID &&
      corporation.ID !== "select" &&
      corporation.ID !== selectOption.ID
    ) {
      getUploadedGridData();
    } else {
      setUploadedData([]);
    }
  }, [
    fromDate,
    toDate,
    corporation,
    statusFilter,
    enqueueSnackbar,
    transaction,
  ]);

  const getExtensionFromContentType = (contentType) => {
    if (contentType.includes("text/csv")) return "csv";
    if (contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) return "xlsx";
    if (contentType.includes("application/vnd.ms-excel")) return "xls";
    if (contentType.includes("text/plain")) return "txt";
    return "xlsx"; // default fallback
  };

  const handleDownload = async (fileTypeName, id) => {
    if (getUserApprovalType !== 3) {
      enqueueSnackbar("You don't have Delete Approvals", {
        variant: "warning",
      });
      return;
    }
    try {
      const fileTypes = fileTypeName;
      // ["summary", "division"]

      for (const fileType of fileTypes) {
        const { blob, contentType } = await downloadFileAPI(token, fileType, id);
        const ext = getExtensionFromContentType(contentType);

        // Create downloadable link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${fileType}_${id}.${ext}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleDialogOpen = async (row, status) => {
    if (getUserApprovalType !== 3) {
      enqueueSnackbar(
        "Only Approval users can Do this, please check your reporting person",
        { variant: "warning" }
      );
      return;
    }

    const attentionCheck = await getDeleteAttentionDetails(row.id, token);
    if (
      attentionCheck.statusCode === 200 &&
      attentionCheck.isAllowed === false
    ) {
      setSelectedRow(row);
      setAttentionDialogeOpen(true);
    } else {
      await handleDelete(row.id, row, status);
    }
  };

  const handleDelete = async (uploadId, row, status) => {
    try {
      if (!privileges.Delete) {
        enqueueSnackbar("You don't have Delete privileges", {
          variant: "warning",
        });
        return;
      }
      if (getUserApprovalType !== 3) {
        enqueueSnackbar(
          "Only Approval users can Delete the DCP files, please check your reporting person",
          { variant: "warning" }
        );
        return;
      }

      if (row.billPaymentCount > 0) {
        const billEntry = getBillPrivileges.find(
          (b) => b.MenuID === billPrivileges?.BillPayment
        );

        if (!billEntry.Delete) {
          enqueueSnackbar("You don't have Delete Bill Payment Privileges ", {
            variant: "warning",
          });
          return;
        }
        if (row.billEntryCount > 0) {
          const billEntryPrivilege = getBillPrivileges.find(
            (b) => b.MenuID === billPrivileges?.BillEntry
          );

          if (!billEntryPrivilege?.Delete) {
            enqueueSnackbar("You don't have Delete Bill Entry Privileges", {
              variant: "warning",
            });
            return;
          }
        }
      }
      let message = "";

      if (status === "delete") {
        message = "Are you sure you want to delete this entry?";
      } else if (status === "attentionClick") {
        message = "Duplicate not allowed. Do you want to delete this entry?";
      }

      // const confirmDelete = window.confirm(
      //   "Duplicate not allowed. Do you want to delete this entry? "
      // );
      const confirmDelete = window.confirm(message);
      if (!confirmDelete) return;

      const response = await deleteUploadList(token, uploadId);

      if (response.statusCode === 400 || response.statusCode === 404) {
        enqueueSnackbar(`${response.status}`, { variant: "warning" });
        return;
      }
      if (response?.statusCode === 200 || response?.statusCode === "success") {
        enqueueSnackbar("Deleted successfully", { variant: "success" });

        setUploadedData((prev) =>
          prev.filter(
            (item) => item.id !== uploadId && item.upload_id !== uploadId
          )
        );
      }
    } catch (error) {
      enqueueSnackbar("Error deleting file", { variant: "error" });
    }
  };

  const handleCellClick = async (type, row) => {
    const fmt = (d) => (d ? d.split("T")[0] : "-");
    const range = await getMinAndMaxDate(row.id, token);

    const { billMinDate, billMaxDate, paymentMinDate, paymentMaxDate } = range;

    const base = `${BillScreenURL}/payable`;
    const common = `${urlInfo}/${authId}/${menuID}/${corporation.ID}/${row.vendorId}/${row.vendorName}`;

    if (type === "billEntryCount") {
      setBillScreenUrlDialog(
        `${base}/billentry/${common}/${fmt(billMinDate)}/${fmt(
          billMaxDate
        )}/pop-up/true`
      );
      setBillName("Bill Entry");
    } else if (type === "billPaymentCount") {
      setBillScreenUrlDialog(
        `${base}/billpayment/${common}/${fmt(paymentMinDate)}/${fmt(
          paymentMaxDate
        )}/pop-up/true`
      );
      setBillName("Bill Payments");
    }
  };

  const columns = [
    { columnName: "Corporation", dataKey: "corporationName", width: 200 },
    { columnName: "DCP Summary", dataKey: "dcpSummary", width: 100 },
    {
      columnName: "DCP Division Summary",
      dataKey: "dcpDivisionSummary",
      width: 100,
    },
    { columnName: "Imported Date", dataKey: "importTimestamp", width: 180 },
    { columnName: "Imported By", dataKey: "importedBy", width: 200 },
    { columnName: "Status", dataKey: "status", width: 120 },
    { columnName: "No. of Bills", dataKey: "billEntryCount", width: 140 },
    {
      columnName: "No. of Bill Payments",
      dataKey: "billPaymentCount",
      width: 150,
    },
    { columnName: "Action", dataKey: "actions", width: 120 },
  ];
  const ValueCell = memo(({ valueObj }) => {
    const displayValue =
      typeof valueObj === "object" && valueObj !== null
        ? valueObj.day || "-"
        : valueObj || "-";
    return <span title={displayValue}>{displayValue}</span>;
  });

  if (!privileges.View) {
    return (
      <SnackbarProvider
        maxSnack={1}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={2000}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#fff",
            p: 3,
          }}
        >
          <Alert
            severity="warning"
            sx={{
              "& .MuiAlert-message": {
                fontSize: "18px",
                fontFamily: "'Poppins', sans-serif !important",
              },
            }}
          >
            You don't have view privileges to access this page.
          </Alert>
        </Box>
      </SnackbarProvider>
    );
  }

  return (
    <SnackbarProvider
      maxSnack={1}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      autoHideDuration={2000}
      // ContentProps={{
      //   sx: {
      //     fontFamily: "'Poppins', sans-serif !important",
      //     bgcolor:"red"
      //   },
      // }}
      classes={{
        contentRoot: "customSnack",
      }}
    >
      <Box
        sx={{
          width: "100%",
          gap: 3,
          alignItems: "center",
          background: "#fff",
          p: 1,
          height: "100%",
        }}
      >
        <Stack
          direction="row"
          sx={{
            width: "100%",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
          }}
        >
          {/* Corporation */}
          <Typography sx={{ fontSize: 13 }}>
            Corporation{" "}
            <Typography component="span" color="error" sx={{ fontSize: 13 }}>
              *
            </Typography>
          </Typography>
          <Autocomplete
            disableClearable
            size="small"
            sx={{ flexGrow: 1, fontSize: 13, minWidth: 180 }}
            value={corporation}
            onChange={(event, newValue) => setCorporation(newValue)}
            options={corporations}
            getOptionLabel={(option) =>
              preferences.isLegalName ? option.LegalName : option.Name
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Select corporation" />
            )}
          />

          {/* Transactions */}
          <Typography sx={{ fontSize: 13 }}>
            View Transactions{" "}
            <Typography component="span" color="error" sx={{ fontSize: 13 }}>
              *
            </Typography>
          </Typography>
          <Autocomplete
            disableClearable
            size="small"
            sx={{ fontSize: 13, minWidth: 200 }}
            value={transaction}
            onChange={(event, newValue) => {
              setTransaction(newValue);
              // if (newValue?.actualValue?.start) setFromDate(newValue.actualValue.start);
              // if (newValue?.actualValue?.end) setToDate(newValue.actualValue.end);
              setTempFromDate(newValue.actualValue.start || null);
              setTempToDate(newValue.actualValue.end || null);
            }}
            options={transactions}
            getOptionLabel={(option) => option.date}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select transaction" />
            )}
          />
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography sx={{ fontSize: 13 }}>
              From{" "}
              <Typography component="span" color="error" sx={{ fontSize: 13 }}>
                *
              </Typography>
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={tempFromDate}
                onChange={(v, context) => {
                  // Save the current valid tempFromDate BEFORE changing it
                  // Only save if current value is valid AND we don't already have a valid saved value
                  // This prevents overwriting the saved value with invalid dates during typing
                  if (tempFromDate?.isValid()) {
                    if (
                      !prevTempFromDate.current ||
                      (typeof prevTempFromDate.current.isValid === "function" &&
                        !prevTempFromDate.current.isValid())
                    ) {
                      prevTempFromDate.current = tempFromDate;
                    }
                  }

                  setTempFromDate(v);

                  if (context.source === "view") {
                    if (tempToDate && v?.isValid() && v.isAfter(tempToDate)) {
                      enqueueSnackbar(
                        "From Date cannot be greater than To Date",
                        { variant: "warning" }
                      );

                      // revert to previous valid date (use state variable from getUploadedGridData)
                      const revertDate =
                        prevValidFromDate ||
                        prevTempFromDate.current ||
                        prevFromDate.current;
                      if (revertDate) {
                        setTempFromDate(revertDate);
                      }
                      return;
                    }
                    // If valid, update fromDate (prevTempFromDate will be updated in onBlur)
                    if (v?.isValid()) {
                      setFromDate(v);
                    }
                  }
                }}
                slotProps={{
                  textField: {
                    ...sharedProps,
                    size: "small",
                    onFocus: () => {
                      // Save the current valid date before user starts editing
                      if (tempFromDate?.isValid()) {
                        prevTempFromDate.current = tempFromDate;
                      }
                    },
                    onBlur: (e) => {
                      // Capture current values
                      const currentFromDate = tempFromDate;
                      const currentToDate = tempToDate;
                      // Use state variable for previous valid date (updated in getUploadedGridData)
                      const savedRevertDate =
                        prevValidFromDate ||
                        prevTempFromDate.current ||
                        prevFromDate.current;

                      // Check if date is invalid (not valid dayjs object or after toDate)
                      const isInvalid =
                        !currentFromDate ||
                        !currentFromDate.isValid() ||
                        (currentToDate &&
                          currentFromDate.isValid() &&
                          currentFromDate.isAfter(currentToDate));

                      if (isInvalid) {
                        if (
                          currentToDate &&
                          currentFromDate?.isValid() &&
                          currentFromDate.isAfter(currentToDate)
                        ) {
                          enqueueSnackbar(
                            "From Date cannot be greater than To Date",
                            { variant: "warning" }
                          );
                        }

                        // revert to previous valid tempFromDate (the date that was last used in getUploadedGridData)
                        if (savedRevertDate) {
                          // Use setTimeout to ensure revert happens after DatePicker's internal state updates
                          setTimeout(() => {
                            setTempFromDate(savedRevertDate);
                          }, 0);
                        }
                        return;
                      }

                      // Save valid tempFromDate
                      prevTempFromDate.current = currentFromDate;
                      setFromDate(currentFromDate);
                      setTransaction(
                        transactions.find((t) => t.date === "Range")
                      );
                    },
                    // onKeyDown: (e) => {
                    //   if (e.key === "Enter" && tempFromDate?.isValid()) {
                    //     if (tempToDate && tempFromDate.isAfter(tempToDate)) {
                    //       enqueueSnackbar(
                    //         "From Date cannot be greater than To Date",
                    //         { variant: "warning" }
                    //       );

                    //       // revert
                    //       setTempFromDate(tempFromDate);
                    //       return;
                    //     }
                    //     setFromDate(tempFromDate);
                    //   }
                    // },
                  },
                }}
              />
            </LocalizationProvider>

            <Typography sx={{ fontSize: 13 }}>
              To{" "}
              <Typography component="span" color="error" sx={{ fontSize: 13 }}>
                *
              </Typography>
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={tempToDate}
                onChange={(v, context) => {
                  // Save the current valid tempToDate BEFORE changing it
                  // Only save if current value is valid AND we don't already have a valid saved value
                  // This prevents overwriting the saved value with invalid dates during typing
                  if (tempToDate?.isValid()) {
                    if (
                      !prevTempToDate.current ||
                      (typeof prevTempToDate.current.isValid === "function" &&
                        !prevTempToDate.current.isValid())
                    ) {
                      prevTempToDate.current = tempToDate;
                    }
                  }

                  setTempToDate(v);

                  if (context.source === "view") {
                    if (
                      tempFromDate &&
                      v?.isValid() &&
                      v.isBefore(tempFromDate)
                    ) {
                      enqueueSnackbar(
                        "To Date cannot be smaller than From Date",
                        { variant: "warning" }
                      );

                      // revert to previous valid date (use state variable from getUploadedGridData)
                      const revertDate =
                        prevValidToDate ||
                        prevTempToDate.current ||
                        prevToDate.current;
                      if (revertDate) {
                        setTempToDate(revertDate);
                      }
                      return;
                    }
                    // If valid, update toDate (prevTempToDate will be updated in onBlur)
                    if (v?.isValid()) {
                      setToDate(v);
                    }
                  }
                }}
                slotProps={{
                  textField: {
                    ...sharedProps,
                    size: "small",
                    onFocus: () => {
                      // Save the current valid date before user starts editing
                      // This ensures we always have the date that was there before editing started
                      if (tempToDate?.isValid()) {
                        prevTempToDate.current = tempToDate;
                      } else {
                        // If current date is invalid, use the last known valid date
                        if (!prevTempToDate.current && prevToDate.current) {
                          prevTempToDate.current = prevToDate.current;
                        }
                      }
                    },
                    onBlur: (e) => {
                      // Capture current values
                      const currentToDate = tempToDate;
                      const currentFromDate = tempFromDate;
                      // Use state variable for previous valid date (updated in getUploadedGridData)
                      const savedRevertDate =
                        prevValidToDate ||
                        prevTempToDate.current ||
                        prevToDate.current;

                      // Check if date is invalid (not valid dayjs object or before fromDate)
                      const isInvalid =
                        !currentToDate ||
                        !currentToDate.isValid() ||
                        (currentFromDate &&
                          currentToDate.isValid() &&
                          currentToDate.isBefore(currentFromDate));

                      if (isInvalid) {
                        if (
                          currentFromDate &&
                          currentToDate?.isValid() &&
                          currentToDate.isBefore(currentFromDate)
                        ) {
                          enqueueSnackbar(
                            "To Date cannot be smaller than From Date",
                            { variant: "warning" }
                          );
                        }

                        // revert to previous valid tempToDate (the date that was last used in getUploadedGridData)
                        if (savedRevertDate) {
                          // Use setTimeout to ensure revert happens after DatePicker's internal state updates
                          setTimeout(() => {
                            setTempToDate(savedRevertDate);
                          }, 0);
                        }
                        return;
                      }

                      // Save valid tempToDate
                      prevTempToDate.current = currentToDate;
                      setTransaction(
                        transactions.find((t) => t.date === "Range")
                      );
                      setToDate(currentToDate);
                    },
                    // onKeyDown: (e) => {
                    //   if (e.key === "Enter" && tempToDate?.isValid()) {
                    //     if (tempFromDate && tempToDate.isBefore(tempFromDate)) {
                    //       enqueueSnackbar(
                    //         "To Date cannot be smaller than From Date",
                    //         { variant: "warning" }
                    //       );

                    //       // revert
                    //       setTempToDate(tempToDate);
                    //       return;
                    //     }
                    //     setToDate(tempToDate);
                    //   }
                    // },
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
          {/* Status */}
          <Typography sx={{ fontSize: 13 }}>Status </Typography>
          <Autocomplete
            disableClearable
            size="small"
            sx={{ flexGrow: 1, maxWidth: 150 }}
            value={statusFilter}
            onChange={(event, newValue) => setStatusFilter(newValue)}
            options={statuses}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select status" />
            )}
          />
          {/* Import Button */}
          <Button
            disabled={
              !corporation ||
              corporation?.ID === "select" ||
              corporation?.ID === selectOption.ID
            }
            sx={{
              bgcolor: "#bfe9fb",
              // p: 1.5,
              borderRadius: 2,
              color: "black",
              textTransform: "none",
              fontSize: 12,
              "&:disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
            // onClick={handleClickOpen}
            onClick={() => {
              if (!privileges.Create) {
                enqueueSnackbar("You don’t have privileges to import files.", {
                  variant: "warning",
                });
                return;
              }

              if (getUserApprovalType !== 3) {
                enqueueSnackbar(
                  "Only Approval users can upload the DCP files, please check your reporting person",
                  { variant: "warning" }
                );
                return;
              }

              setImportDialogOpen(true);
            }}
          >
            Import DCP files
          </Button>
        </Stack>
        <Box height={"calc(100% - 85px)"}>
          <TableContainer
            sx={{ mt: 3, height: "100%", border: "1px solid lightgrey" }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#d7d7d7" }}>
                  {columns.map((col, idx) => (
                    <TableCell
                      key={idx}
                      style={{
                        backgroundColor: "#d7d7d7",
                        borderRight: "1px solid white",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: 12,
                        maxWidth: col.width,
                      }}
                    >
                      <span title={col.columnName}>{col.columnName}</span>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {uploadedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={{ fontSize: 12, color: "#999" }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  uploadedData
                    .slice((page - 1) * rowsPerPage, page * rowsPerPage)
                    .map((row, rowIndex) => (
                      <TableRow key={row.upload_id || rowIndex}>
                        {columns.map((col, idx) => (
                          <TableCell
                            align={
                              col.columnName === "Corporation" ||
                              col.columnName === "Imported Date" ||
                              col.columnName === "Imported By"
                                ? "left"
                                : "center"
                            }
                            key={idx}
                            sx={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: 12,
                              fontWeight: col.dataKey === "status" ? 500 : 500,
                              color:
                                col.dataKey === "status"
                                  ? row.status === "Posted"
                                    ? "green"
                                    : row.status === "In Progress"
                                    ? "orange"
                                    : row.status === "Attention Needed"
                                    ? "red"
                                    : "black"
                                  : "#626262",
                              borderRight: "1px solid #f2f2f2",
                              maxWidth: col.width,
                              cursor:
                                col.dataKey === "status" &&
                                row.status === "Attention Needed"
                                  ? "pointer"
                                  : "default",
                              textDecoration:
                                col.dataKey === "status" &&
                                row.status === "Attention Needed"
                                  ? "underline"
                                  : "none",
                            }}
                          >
                            {/* ✅ Handle green tick for DCP fields */}
                            {col.dataKey === "dcpSummary" ||
                            col.dataKey === "dcpDivisionSummary" ? (
                              <span
                                title={`Download ${
                                  col.dataKey === "dcpSummary"
                                    ? "summary file"
                                    : "division file"
                                }`}
                                style={{
                                  fontSize: 16,
                                  padding: 0,
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleDownload(
                                    col.dataKey === "dcpSummary"
                                      ? ["summary"]
                                      : ["division"],
                                    row.id || row.upload_id
                                  )
                                }
                              >
                                {row[col.dataKey] ? "✅" : "-"}
                              </span>
                            ) : col.dataKey === "actions" ? (
                              <Stack direction={"row"} gap={1}>
                                <IconButton
                                  onClick={() =>
                                    handleDownload(
                                      ["summary", "division"],
                                      row.id || row.upload_id
                                    )
                                  }
                                >
                                  {/* <FileDownloadIcon /> */}
                                  <img
                                    src={downloadIcon}
                                    alt="download"
                                    style={{ width: 22, height: 22 }}
                                  />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    handleDelete(row.id, row, "delete")
                                  }
                                  // onClick={() => handleDialogOpen(row, "delete")}
                                >
                                  <img
                                    src={deleteIcon}
                                    alt="delete"
                                    style={{ width: 22, height: 22 }}
                                  />
                                </IconButton>
                              </Stack>
                            ) : col.dataKey === "status" ? (
                              <span
                                sx={{
                                  fontWeight: 500,
                                  color:
                                    row.status === "Posted"
                                      ? "green"
                                      : row.status === "In Progress"
                                      ? "orange"
                                      : row.status === "Attention Needed"
                                      ? "red"
                                      : "black",
                                  cursor:
                                    row.status === "Attention Needed"
                                      ? "pointer"
                                      : "default",
                                  textDecoration:
                                    row.status === "Attention Needed"
                                      ? "underline"
                                      : "none",
                                  fontSize: "11px",
                                }}
                                onClick={() => {
                                  if (row.status === "Attention Needed") {
                                    handleDialogOpen(row, "attentionClick");
                                  }
                                }}
                                title={
                                  row.status === "Attention Needed"
                                    ? "Click to view details"
                                    : ""
                                }
                              >
                                {row.status || "-"}
                              </span>
                            ) : col.dataKey === "importTimestamp" ? (
                              <span>
                                {row.importTimestamp
                                  ? new Date(
                                      row.importTimestamp
                                    ).toLocaleDateString("en-US", {
                                      month: "2-digit",
                                      day: "2-digit",
                                      year: "numeric",
                                    })
                                  : "-"}
                              </span>
                            ) : col.dataKey === "billEntryCount" ||
                              col.dataKey === "billPaymentCount" ? (
                              <span
                                style={{
                                  cursor:
                                    row.billEntryCount > 0 ||
                                    row.billPaymentCount > 0
                                      ? "pointer"
                                      : null,
                                  color:
                                    row.billEntryCount > 0 ||
                                    row.billPaymentCount > 0
                                      ? "blue"
                                      : null,
                                  textDecoration:
                                    row.billEntryCount > 0 ||
                                    row.billPaymentCount > 0
                                      ? "underline"
                                      : "none",
                                }}
                                onClick={() => {
                                  if (
                                    row.billEntryCount > 0 ||
                                    row.billPaymentCount > 0
                                  ) {
                                    handleCellClick(col.dataKey, row);
                                  }
                                }}
                                title={`View ${col.columnName} details`}
                              >
                                {row[col.dataKey] || "-"}
                              </span>
                            ) : (
                              <ValueCell valueObj={row[col.dataKey]} />
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            // border={"1px solid red"}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            pr={2}
            pt={0.5}
          >
            <Pagination
              count={Math.ceil(uploadedData.length / rowsPerPage)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              size="small"
            />
          </Box>
        </Box>

        {loading && <Loader loading={loading} />}

        {Boolean(billScreenUrlDialog) && (
          <BillScreenViewer
            open={Boolean(billScreenUrlDialog)}
            onClose={() => setBillScreenUrlDialog(null)}
            dataURL={billScreenUrlDialog}
            heading={billName}
          />
        )}

        {importDialogOpen && (
          <ImportFilesDialog
            open={importDialogOpen}
            onClose={() => setImportDialogOpen(false)}
            corporation={corporation}
            setCorporation={setCorporation}
            getUploadedGridData={getUploadedGridData}
          />
        )}

        {attentionDialogeOpen && (
          <AttentionNeededDialog
            open={attentionDialogeOpen}
            onClose={() => setAttentionDialogeOpen(false)}
            corporation={corporation}
            selectedRow={selectedRow}
            getUploadedGridData={getUploadedGridData}
          />
        )}
      </Box>
    </SnackbarProvider>
  );
};

export default Dcp;
